import { Alert, AlertDescription, AlertTitle } from '@/components/_ui/Alert'
import { AspectRatio } from '@/components/_ui/AspectRatio'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import T from '@/components/Helpers/Translate'
import { useModals } from '@/components/ModalManager/useModals'
import type {
  CosmicFreeItemPromoType,
  CosmicPurchaseOptionType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGetCustomerProductPricing } from '@/lib/hooks/helpers/useGetCustomerProductPricing'
import { usePriceHandler } from '@/lib/hooks/helpers/usePriceHandler'
import { cn } from '@/lib/utils'
import { isAvailableForCustomerType } from '@/lib/utils/user/isAvailableForCustomerType'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import type { JSX } from 'react'
import { useEffect } from 'react'
import { customerTypesElegibleForSubscriptionDiscounts } from '@/constants/customerTypesElegibleForSubscriptionDiscounts'
import { useLoadProductOverwrittenTerms } from '../../../../lib/hooks/helpers/useGetOverwriteTerms'
import styles from './ProductPurchase.module.css'

type Props = {
  purchaseOptions?: CosmicPurchaseOptionType[]
  selectedVariation: CosmicVariationType | null
  selectedPurchaseOption: string | null
  setSelectedPurchaseOption: (selectedPurchaseOption: string) => void
  subscriptionDiscount?: number | null
  isQuickAdd?: boolean
}

export const ProductPurchaseOptions = ({
  purchaseOptions,
  selectedPurchaseOption,
  setSelectedPurchaseOption,
  selectedVariation,
  subscriptionDiscount,
  isQuickAdd,
}: Props) => {
  const {
    app: { marketExceptions },
    user,
  } = useGlobalStore()
  const { displayPrice } = useGetCustomerProductPricing()

  const { priceHandler } = usePriceHandler()
  const { modals } = useModals()
  const { getAssociatePricingData } = useLoadProductOverwrittenTerms()

  // This makes sure there is always a default option selected, when omitSubscriptionsAppWide is true.
  // If the default is set to subscription in cosmic, this will make sure the OTP option (the only option available if subs are removed) is selected by default.
  useEffect(() => {
    if (marketExceptions?.omitSubscriptionsAppWide) {
      const otpOption = purchaseOptions?.find(
        (option) => option.key === 'onetime',
      )

      if (otpOption) {
        setSelectedPurchaseOption(otpOption.key)
      }
    }
  }, [marketExceptions, purchaseOptions, setSelectedPurchaseOption])

  const purchaseOptionItem = (purchaseOption: CosmicPurchaseOptionType) => {
    if (!selectedVariation) return null
    if (!purchaseOption) return null
    const isAssociate = user.customerType === 'Associate'
    const { header, isEnable, key: type, bulletPoints } = purchaseOption

    const correctPricing = isAssociate
      ? getAssociatePricingData(selectedVariation?.pricing)
      : selectedVariation?.pricing

    const isSubscription = type === 'subscription'
    const isSelected = selectedPurchaseOption === type

    const openPromoModal = (promoModal: CosmicFreeItemPromoType) => {
      modals.openModal({
        type: 'modal',
        modalId: promoModal.title,
        modal: {
          className: 'max-w-3xl',
          children: (
            <div>
              <div className="grid grid-cols-1 gap-x-10 md:grid-cols-2">
                <AspectRatio
                  ratio={1}
                  className="image relative flex overflow-hidden rounded-lg bg-cover"
                >
                  <Image
                    fill
                    alt={promoModal.altText}
                    src={`${promoModal.image}`}
                  />
                </AspectRatio>
                <div className="info flex-column flex flex-col p-2">
                  <div className="title text-[24px] font-semibold">
                    {promoModal.title}
                  </div>
                  {/* <div className={styles.subtitle}>{promoModal.detail}</div> */}
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{
                      __html: promoModal.detail,
                    }}
                  />
                </div>
              </div>
            </div>
          ),
        },
      })
    }

    const renderPurchaseOptionList = () => {
      const formattedList = bulletPoints
        .map((item) => {
          if (
            !isAvailableForCustomerType({
              customerType: user.customerType,
              availableFor: item.availableFor,
            })
          ) {
            return null
          }

          const promoModal = item.freeItemPromo

          return (
            <button
              type="button"
              className={cn(
                `mt-0 w-full list-none text-left break-words`,
                'relative block',
                bulletPoints.length > 1 &&
                  'pl-3 before:absolute before:top-0 before:left-0 before:content-["•"]',
                'hover:text-accent-foreground focus:ring-2 focus:ring-accent focus:outline-none',
              )}
              key={item.descriptionHtml}
              dangerouslySetInnerHTML={{
                __html: item.descriptionHtml,
              }}
              aria-label={item.descriptionHtml}
              onClick={
                promoModal ? () => openPromoModal(promoModal) : undefined
              }
              onKeyDown={(event) => {
                if (
                  promoModal &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  openPromoModal(promoModal)
                }
              }}
            />
          )
        })
        .filter((item): item is JSX.Element => item !== null) // Filter out null values

      return (
        <div className={cn('flex w-full')}>
          {' '}
          <ul className={cn('w-full', formattedList.length > 1 ? 'pl-1' : '')}>
            {formattedList}
          </ul>
        </div>
      )
    }

    if (isSubscription && marketExceptions?.omitSubscriptionsAppWide) {
      return null
    }

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (isEnable) {
            // Never set a disabled variation as selected
            setSelectedPurchaseOption(type)
          }
        }}
        onKeyDown={(event) => {
          if (isEnable && (event.key === 'Enter' || event.key === ' ')) {
            setSelectedPurchaseOption(type)
          }
        }}
        className={cn(
          `relative mt-3 flex w-full cursor-pointer flex-col rounded-lg border-solid px-6 py-5 sm:mt-5`,
          !isEnable && 'pointer-events-none cursor-none opacity-50',
          isSelected
            ? 'border-2 border-[#9DCBA0] bg-[#9DCBA033]'
            : 'border border-[#99BCDF99] hover:bg-accent',
          isQuickAdd && 'cursor-default p-3 text-sm',
        )}
        data-test={`product-purchase-option-${type}`}
      >
        {isSubscription && (
          <div className="absolute -top-6 right-2 h-[22px] rounded-t-lg bg-[#173962E5] px-2 pt-1 text-[10px] font-semibold text-white">
            <T>most_popular</T>
          </div>
        )}
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <p className="font-bold">{header}</p>
            {renderPurchaseOptionList()}
          </div>
          <div className="flex flex-col">
            <div className="flex justify-between">
              <span className="font-bold">
                {!isEnable && (
                  <span>
                    - <T>unavailable</T>
                  </span>
                )}
              </span>

              <div className="flex flex-col md:flex-row">
                {user.customerType !== 'Customer' && (
                  <div
                    className="text-center text-xs line-through md:my-auto md:mr-[.5rem]"
                    data-test="product-one-time-discounted-price"
                  >
                    {displayPrice({
                      forCustomerType: 'Customer',
                      pricingData: selectedVariation?.pricing,
                    })}
                  </div>
                )}
                <div data-test="product-one-time-purchase-price">
                  {priceHandler({
                    forCustomerType: user.customerType,
                    pricingData: correctPricing,
                    subscriptionDiscount:
                      purchaseOption.key === 'subscription' &&
                      customerTypesElegibleForSubscriptionDiscounts.includes(
                        user.customerType,
                      )
                        ? subscriptionDiscount ?? 0
                        : 0,
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-end text-right text-sm md:flex-row">
              {marketExceptions?.tax?.inclusive ? (
                <>
                  <div className="mr-1">
                    <T>VAT</T>
                  </div>
                  <div>
                    <T>included</T>
                  </div>
                </>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!purchaseOptions) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Unable to load product purchase options.',
          },
        }}
      />
    )
  }

  if (!selectedVariation) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Unable to load product pricing.',
          },
        }}
      />
    )
  }

  if (!selectedVariation.sku) {
    return (
      <Alert>
        <AlertTitle>
          <T>sorry_this_combination_is_not_available_for_purchase</T>
        </AlertTitle>
        <AlertDescription>
          <T>please_choose_a_different_combination</T>
        </AlertDescription>
      </Alert>
    )
  }

  if (selectedVariation.pricing === null) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Unable to load product pricing.',
          },
        }}
      />
    )
  }

  return (
    <div className="flex w-full flex-col" data-test="product-purchase">
      {!isQuickAdd && (
        <span className="pb-3 text-sm text-[#0B3B6199]">
          <T>select_purchase_option</T>
        </span>
      )}

      {purchaseOptions?.map((option) => purchaseOptionItem(option))}
    </div>
  )
}
